import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { useDistricts, useProvinces } from '../../lib/geo'
import { titleCase } from '../../lib/format'
import type { CourseSummary, Organization, RoleCoverage } from '../../types'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, Select, Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import DownloadButton from '../../components/DownloadButton'
import { CheckIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/org_detail.html + its view
 * admin/routes.py:org_detail(), plus the three lifecycle views
 * organization_suspend/dissolve/restore(). Data comes from the same
 * GET /organizations/:orgId (-> {organization, members}) gov/OrgDetail.tsx
 * already uses - super_admin's data scope is unrestricted so it's just as
 * usable here - rather than a separate admin-only detail route (the
 * /admin/orgs/:orgId one exists but returns a slightly different shape and
 * isn't what this page was asked to use).
 *
 * The Directors/Executives card and the inline org.documents (doc_key -> url)
 * card from org_detail.html are intentionally not ported: the task spec's
 * section list didn't call for them, and the fuller "Organisation Documents"
 * manager (org_docs.manage, linked from the sidebar below) already supersedes
 * the latter. */

const ORG_TYPES = [
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'company', label: 'Company' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'family', label: 'Family Enterprise' },
]

// Fields org_detail.html shows that aren't on the shared Organization type -
// kept as a local extension rather than editing types.ts, same call
// UserDetail.tsx made for its own admin-only extra fields (AdminUserProfile).
interface AdminOrganization extends Organization {
  mobile_money?: string
  staff_size?: number | string
  annual_revenue?: number | string
  years_in_business?: number | string
  minerals?: string[]
}

// GET /organizations/:orgId's members come straight from the org_members
// RTDB node (rtdb_service.list_org_members via org_service.list_members),
// which add_member() only ever writes {uid, org_id, role, roles, joined_at,
// status} to - no full_name/email snapshot. Jinja's org_detail() enriches
// each row with a separate _members_for_org_detail() join the JSON API
// doesn't replicate, so full_name/email are typed optional here and the
// row below falls back to the uid (as gov/OrgDetail.tsx's own member table
// already does) whenever they're absent.
interface OrgMember {
  uid: string
  full_name?: string
  email?: string
  role?: string
  roles?: string[]
  status?: string
}

interface OrgDetailResponse { organization: AdminOrganization; members: OrgMember[] }

// GET /organizations/:orgId/role-coverage wraps the coverage report under a
// `coverage` key (jsonify(ok=True, coverage=coverage)) - gov/OrgDetail.tsx
// types this same call as `RoleCoverage` directly and reads
// coverageQuery.data?.rows / .mandatory_filled off the top level, which
// doesn't match that wire shape (the real fields live one level down, under
// .coverage). That looks like a pre-existing bug there - its coverage table
// would always render empty. Typed correctly here so this page's panel
// actually shows data.
interface RoleCoverageResponse { ok: boolean; coverage: RoleCoverage }

type Tone = 'danger' | 'info' | 'warning' | 'success'

function roleTone(role?: string): Tone {
  if (role === 'super_admin') return 'danger'
  if (['gov_director', 'gov_regional', 'gov_inspector'].includes(role ?? '')) return 'info'
  if (role === 'team_leader') return 'warning'
  return 'success'
}

interface TrainingProgram { course_id: string; is_mandatory?: boolean; assigned_at?: string }

type Tab = 'overview' | 'members' | 'compliance' | 'training' | 'settings'

export default function AdminOrgDetail() {
  const { orgId } = useParams<{ orgId: string }>()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [tab, setTab] = useState<Tab>('overview')
  const [editing, setEditing] = useState(false)
  const [courseToAssign, setCourseToAssign] = useState('')

  const orgQuery = useQuery({
    queryKey: ['admin', 'org', orgId],
    queryFn: () => api.get<OrgDetailResponse>(`/organizations/${orgId}`),
    enabled: Boolean(orgId),
  })
  const coverageQuery = useQuery({
    queryKey: ['admin', 'org-coverage', orgId],
    queryFn: () => api.get<RoleCoverageResponse>(`/organizations/${orgId}/role-coverage`),
    enabled: Boolean(orgId) && orgQuery.data?.organization.type === 'cooperative',
  })
  // Reuses /gov/organizations/:id/training verbatim - already allows
  // super_admin, same as every other "gov/*" endpoint this portal borrows.
  const trainingQuery = useQuery({
    queryKey: ['admin', 'org-training', orgId],
    queryFn: () => api.get<{ ok: boolean; programs: TrainingProgram[] }>(`/gov/organizations/${orgId}/training`),
    enabled: Boolean(orgId),
  })
  const coursesQuery = useQuery({
    queryKey: ['learning', 'courses'],
    queryFn: () => api.get<{ ok: boolean; courses: CourseSummary[] }>('/learning/courses'),
  })

  const assignTraining = useMutation({
    mutationFn: (course_id: string) => api.post(`/gov/organizations/${orgId}/training`, { course_id, is_mandatory: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'org-training', orgId] })
      setCourseToAssign('')
    },
  })
  const removeTraining = useMutation({
    mutationFn: (course_id: string) => api.del(`/gov/organizations/${orgId}/training/${course_id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'org-training', orgId] }),
  })

  const suspend = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string }>(`/admin/orgs/${orgId}/suspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'org', orgId] }),
  })
  const restore = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string }>(`/admin/orgs/${orgId}/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'org', orgId] }),
  })
  const dissolve = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string }>(`/admin/orgs/${orgId}/dissolve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'org', orgId] }),
  })

  if (!orgId) return <Navigate to="/users" replace />
  if (orgQuery.isError) {
    return (
      <Alert tone="danger">
        {(orgQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/users">Back to organisations</Link></div>
      </Alert>
    )
  }
  if (orgQuery.isLoading || !orgQuery.data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const { organization: org, members } = orgQuery.data
  const orgName = org.name || 'this organisation'
  const coverage = coverageQuery.data?.coverage
  const mutationError = suspend.error ?? restore.error ?? dissolve.error
  const courses = coursesQuery.data?.courses ?? []
  const programs = trainingQuery.data?.programs ?? []
  const assignedIds = new Set(programs.map((p) => p.course_id))

  // "when I click on a company I can see if they have issues" - surfaced as
  // a standing banner above the tabs rather than buried in a card the admin
  // has to think to open. A grandfathered org (no registration_status at
  // all - see enforcement_service.is_compliance_approved) is intentionally
  // NOT flagged here, matching that same rule.
  const issues: string[] = []
  if (org.registration_status && org.registration_status !== 'approved') {
    issues.push(`Compliance is ${titleCase(org.registration_status)} — not yet approved.`)
  }
  if (org.type === 'cooperative' && coverage
      && (coverage.mandatory_filled ?? 0) < (coverage.mandatory_total ?? 0)) {
    issues.push(`Governance coverage: only ${coverage.mandatory_filled ?? 0} of ${coverage.mandatory_total ?? 0} mandatory seats filled.`)
  }
  if (org.status === 'suspended') issues.push('This organisation is currently suspended.')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{org.name || 'Organisation'}</h1>
          <p className="page-head__sub">
            {titleCase(org.type)} · {[titleCase(org.district_id), titleCase(org.region_id)].filter(Boolean).join(', ')}
          </p>
        </div>
        <StatusBadge status={org.status} />
      </div>

      {issues.length > 0 && (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Alert tone="warning">
            <div className="stack" style={{ gap: 'var(--sp-1)' }}>
              {issues.map((msg) => <div key={msg}>{msg}</div>)}
            </div>
          </Alert>
        </div>
      )}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'overview' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('overview')}>
          Overview
        </Button>
        <Button variant={tab === 'members' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('members')}>
          Members ({members.length})
        </Button>
        <Button variant={tab === 'compliance' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('compliance')}>
          Compliance
        </Button>
        <Button variant={tab === 'training' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('training')}>
          Training
        </Button>
        <Button variant={tab === 'settings' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('settings')}>
          Settings
        </Button>
      </div>

      {tab === 'overview' && (
        <Card>
          <CardHeader title="Company Details" />
          <CardBody>
            <div className="grid grid--2">
              <Row label="Company Name" value={org.name || '—'} />
              <Row label="Type" value={titleCase(org.type)} />
              <Row label="Registration No." value={org.registration_no || '—'} />
              <Row label="Tax ID" value={org.tax_id || '—'} />
              <Row label="Region" value={titleCase(org.region_id)} />
              <Row label="District" value={titleCase(org.district_id)} />
              <Row label="Address" value={org.address || '—'} />
              <Row label="Mobile Money" value={org.mobile_money || '—'} />
              {org.staff_size ? <Row label="Staff Size" value={String(org.staff_size)} /> : null}
              {org.annual_revenue ? <Row label="Annual Revenue" value={String(org.annual_revenue)} /> : null}
              {org.years_in_business ? <Row label="Years in Business" value={String(org.years_in_business)} /> : null}
              {org.minerals && org.minerals.length > 0 ? <Row label="Minerals" value={org.minerals.join(', ')} /> : null}
            </div>
          </CardBody>
        </Card>
      )}

      {tab === 'members' && (
        <Card>
          <CardHeader title={`Members (${members.length})`} />
          <CardBody flush>
            {members.length === 0 ? (
              <p className="page-head__sub" style={{ margin: 'var(--sp-4)' }}>No members yet.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Name</th><th>Email</th><th>UID</th><th>Role</th></tr></thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.uid}>
                        <td><Link to={`/users/${m.uid}`}>{m.full_name || `${m.uid.slice(0, 8)}…`}</Link></td>
                        <td>{m.email || '—'}</td>
                        <td><span style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', wordBreak: 'break-all' }}>{m.uid}</span></td>
                        <td><Badge tone={roleTone(m.role)}>{titleCase(m.role)}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {tab === 'compliance' && (
        <div className="stack" style={{ gap: 'var(--sp-6)' }}>
          <Card>
            <CardHeader
              title="Compliance setup"
              sub={<StatusBadge status={org.registration_status || 'active'} />}
              action={
                <Link to={`/compliance/${orgId}`} style={{ fontSize: 'var(--text-xs)', fontWeight: 600 }}>
                  Review compliance →
                </Link>
              }
            />
          </Card>

          {org.type === 'cooperative' && (
            <Card>
              <CardHeader
                title="Governance coverage"
                sub={coverage ? `${coverage.mandatory_filled ?? 0} of ${coverage.mandatory_total ?? 0} mandatory seats filled` : undefined}
              />
              <CardBody flush>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Role</th><th>Requirement</th><th>Filled</th></tr></thead>
                    <tbody>
                      {(coverage?.rows ?? []).map((r) => (
                        <tr key={r.key}>
                          <td>{r.title}</td>
                          <td><Badge tone={r.requirement === 'mandatory' ? 'warning' : 'neutral'}>{r.requirement}</Badge></td>
                          <td>
                            {r.filled ? (
                              <span style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <CheckIcon size={14} /> {r.assignee_count}/{r.min_count}
                              </span>
                            ) : `${r.assignee_count ?? 0}/${r.min_count}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {tab === 'training' && (
        <Card>
          <CardHeader title="Assigned training" sub="Courses mandated for this organisation's members." />
          <CardBody>
            {assignTraining.isError && (
              <div style={{ marginBottom: 'var(--sp-3)' }}>
                <Alert tone="danger">
                  {assignTraining.error instanceof ApiError ? assignTraining.error.message : 'Could not assign that course.'}
                </Alert>
              </div>
            )}
            <div className="stack" style={{ marginBottom: 'var(--sp-4)' }}>
              {programs.length === 0 ? (
                <p className="page-head__sub" style={{ margin: 0 }}>No training assigned yet.</p>
              ) : (
                programs.map((p) => {
                  const course = courses.find((c) => c.course_id === p.course_id)
                  return (
                    <div key={p.course_id} className="row" style={{ justifyContent: 'space-between' }}>
                      <span>{course?.course_name || p.course_id} {p.is_mandatory && <Badge tone="warning">Mandatory</Badge>}</span>
                      <Button size="sm" variant="ghost" onClick={async () => {
                        if (await confirm({
                          title: 'Remove this required course?',
                          message: course?.course_name || p.course_id,
                          destructive: true,
                        })) removeTraining.mutate(p.course_id)
                      }}>Remove</Button>
                    </div>
                  )
                })
              )}
            </div>
            <div className="row" style={{ alignItems: 'flex-end', gap: 'var(--sp-3)' }}>
              <div style={{ flex: 1 }}>
                <Select label="Assign a course" value={courseToAssign} onChange={(e) => setCourseToAssign(e.target.value)}>
                  <option value="">Select…</option>
                  {courses.filter((c) => !assignedIds.has(c.course_id)).map((c) => (
                    <option key={c.course_id} value={c.course_id}>{c.course_name}</option>
                  ))}
                </Select>
              </div>
              <Button
                loading={assignTraining.isPending}
                disabled={!courseToAssign}
                onClick={() => assignTraining.mutate(courseToAssign)}
              >
                Assign
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {tab === 'settings' && (
        <div className="stack" style={{ gap: 'var(--sp-6)' }}>
          <Card>
            <CardHeader
              title="Edit Organisation"
              action={<Button variant="secondary" size="sm" onClick={() => setEditing((s) => !s)}>{editing ? 'Hide' : 'Edit'}</Button>}
            />
            {editing && (
              <CardBody>
                <EditOrgForm org={org} orgId={orgId} onSaved={() => setEditing(false)} />
              </CardBody>
            )}
          </Card>

          <Card>
            <CardHeader title="Organisation tools" />
            <CardBody>
              <div className="stack" style={{ gap: 'var(--sp-2)' }}>
                <DownloadButton
                    path={`/gov/organizations/${orgId}/compliance-report.pdf`}
                    filename="compliance_report.pdf"
                  >
                    Download Compliance &amp; Roles PDF
                  </DownloadButton>
                <a
                  href={`/org-documents/${orgId}?next=${encodeURIComponent(`/organizations/${orgId}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="btn btn--secondary btn--sm btn--block"
                >
                  Organisation Documents
                </a>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Danger zone"
              sub="Suspending hides the org until restored. Dissolving reverts all members to ordinary users — records are kept."
            />
            <CardBody>
              {mutationError && (
                <div style={{ marginBottom: 'var(--sp-3)' }}>
                  <Alert tone="danger">{mutationError instanceof ApiError ? mutationError.message : 'That action failed.'}</Alert>
                </div>
              )}
              <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                <Button
                  variant="danger" size="sm" loading={suspend.isPending}
                  onClick={async () => {
                    if (await confirm({
                      title: `Suspend '${orgName}'?`,
                      message: 'It will be hidden from active lists until you restore it.',
                      destructive: true,
                    })) suspend.mutate()
                  }}
                >
                  Suspend
                </Button>
                <Button
                  variant="secondary" size="sm" loading={restore.isPending}
                  onClick={async () => {
                    if (await confirm({ title: `Restore '${orgName}' to active?` })) restore.mutate()
                  }}
                >
                  Restore
                </Button>
                <Button
                  variant="danger" size="sm" loading={dissolve.isPending}
                  onClick={async () => {
                    if (await confirm({
                      title: `Dissolve '${orgName}'?`,
                      message: 'All members revert to ordinary users and the organisation is archived. '
                        + 'Records are retained and this can be undone by restoring it.',
                      confirmLabel: 'Dissolve',
                      destructive: true,
                    })) dissolve.mutate()
                  }}
                >
                  Dissolve organisation
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

/* Owns its own region/district state, seeded from `org` - this only mounts
 * once the parent's loading guard has already resolved `org`, so reading its
 * fields into useState's initialiser here is safe and needs no effect to
 * "catch up" later (same reasoning CreateUser.tsx's per-tab *Form components
 * rely on for their own local state). */
function EditOrgForm({
  org, orgId, onSaved,
}: { org: AdminOrganization; orgId: string; onSaved: () => void }) {
  const [regionId, setRegionId] = useState(org.region_id ?? '')
  const [districtId, setDistrictId] = useState(org.district_id ?? '')
  const provinces = useProvinces()
  const districts = useDistricts(regionId)
  const qc = useQueryClient()

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<{ ok: boolean; item: Organization }>(`/admin/orgs/${orgId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'org', orgId] })
      onSaved()
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    update.mutate({
      name: String(form.get('name') ?? '').trim(),
      type: String(form.get('type') ?? ''),
      registration_no: String(form.get('registration_no') ?? '').trim(),
      tax_id: String(form.get('tax_id') ?? '').trim(),
      region_id: regionId,
      district_id: districtId,
      mobile_money: String(form.get('mobile_money') ?? '').trim(),
      address: String(form.get('address') ?? '').trim(),
    })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      {update.isError && (
        <Alert tone="danger">{update.error instanceof ApiError ? update.error.message : 'Could not update this organisation.'}</Alert>
      )}

      <div className="grid grid--2">
        <div style={{ gridColumn: '1 / -1' }}>
          <Input label="Name" name="name" defaultValue={org.name} />
        </div>
        <Select label="Type" name="type" defaultValue={org.type || 'cooperative'}>
          {ORG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        <Input label="Registration No." name="registration_no" defaultValue={org.registration_no} />
        <Input label="Tax ID" name="tax_id" defaultValue={org.tax_id} />
        <Select
          label="Region" value={regionId}
          onChange={(e) => { setRegionId(e.target.value); setDistrictId('') }}
        >
          <option value="">None</option>
          {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select
          label="District" value={districtId}
          onChange={(e) => setDistrictId(e.target.value)} disabled={!regionId}
        >
          <option value="">None</option>
          {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Select>
        <Input label="Mobile Money" name="mobile_money" defaultValue={org.mobile_money} />
        <div style={{ gridColumn: '1 / -1' }}>
          <Textarea label="Address" name="address" rows={2} defaultValue={org.address} />
        </div>
      </div>

      <div className="row">
        <Button type="submit" loading={update.isPending}>Save Changes</Button>
      </div>
    </form>
  )
}
